import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { Router } from "@angular/router";
import { LucideDynamicIcon } from "@lucide/angular";
import { LanguageCode } from "../core/language.model";
import { LanguageService } from "../core/language.service";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import { badgeClasses, buttonClasses, cardClasses } from "../shared/ui";

type LocalizedText = Record<LanguageCode, string>;

interface RuleSection {
  icon: string;
  title: LocalizedText;
  rules: LocalizedText[];
}

interface RulesCopy {
  backToMap: string;
  headerTitle: string;
  headerSubtitle: string;
  streakBadge: string;
  hubBadge: string;
  crewBadge: string;
  intro: string;
}

const RULES_COPY: Record<LanguageCode, RulesCopy> = {
  en: {
    backToMap: "Back to map",
    headerTitle: "Game Rules",
    headerSubtitle: "Quick reference for players and crews",
    streakBadge: "Daily streaks",
    hubBadge: "Hub boards",
    crewBadge: "Crew rules",
    intro:
      "LINA is about advancing through islands, learning AI concepts, scoring through stages and returning daily. Each completed island raises your level and unlocks more of the map.",
  },
  pt: {
    backToMap: "Voltar ao mapa",
    headerTitle: "Regras do jogo",
    headerSubtitle: "Referencia rapida para jogadores e crews",
    streakBadge: "Sequencias diarias",
    hubBadge: "Rankings por hub",
    crewBadge: "Regras de crew",
    intro:
      "A LINA e sobre avancar por ilhas, aprender conceitos de IA, pontuar nas fases e voltar diariamente. Cada ilha concluida aumenta seu nivel e libera mais partes do mapa.",
  },
  nl: {
    backToMap: "Terug naar kaart",
    headerTitle: "Spelregels",
    headerSubtitle: "Snelle referentie voor spelers en crews",
    streakBadge: "Dagelijkse streaks",
    hubBadge: "Hubranglijsten",
    crewBadge: "Crewregels",
    intro:
      "LINA draait om vooruitgaan door eilanden, AI-concepten leren, scoren via fases en dagelijks terugkomen. Elk voltooid eiland verhoogt je niveau en ontgrendelt meer van de kaart.",
  },
  sv: {
    backToMap: "Tillbaka till kartan",
    headerTitle: "Spelregler",
    headerSubtitle: "Snabbreferens för spelare och crews",
    streakBadge: "Dagliga streaks",
    hubBadge: "Hubbtopplistor",
    crewBadge: "Crewregler",
    intro:
      "LINA handlar om att ta sig fram genom öar, lära sig AI-koncept, få poäng genom steg och återvända dagligen. Varje klar ö höjer din nivå och låser upp mer av kartan.",
  },
};

const RULE_SECTIONS: RuleSection[] = [
  {
    icon: "star",
    title: {
      en: "Scoring and stages",
      pt: "Pontuacao e fases",
      nl: "Score en fases",
      sv: "Poäng och steg",
    },
    rules: [
      {
        en: "Correct answers add points to the player score.",
        pt: "Respostas corretas adicionam pontos a pontuacao do jogador.",
        nl: "Goede antwoorden voegen punten toe aan de spelersscore.",
        sv: "Rätta svar lägger till poäng till spelarens poäng.",
      },
      {
        en: "Fast correct answers can add up to 3 bonus points.",
        pt: "Respostas corretas rapidas podem adicionar ate 3 pontos bonus.",
        nl: "Snelle goede antwoorden kunnen tot 3 bonuspunten toevoegen.",
        sv: "Snabba rätta svar kan lägga till upp till 3 bonuspoäng.",
      },
      {
        en: "A completed stage cannot be played again.",
        pt: "Uma fase concluida nao pode ser jogada novamente.",
        nl: "Een voltooide fase kan niet opnieuw worden gespeeld.",
        sv: "Ett avslutat steg kan inte spelas igen.",
      },
      {
        en: "When all stages on an island are complete, the island is complete and the next island can unlock.",
        pt: "Quando todas as fases de uma ilha sao concluidas, a ilha fica completa e a proxima pode ser liberada.",
        nl: "Wanneer alle fases op een eiland zijn voltooid, is het eiland klaar en kan het volgende eiland worden ontgrendeld.",
        sv: "När alla steg på en ö är klara är ön klar och nästa ö kan låsas upp.",
      },
    ],
  },
  {
    icon: "flame",
    title: {
      en: "Daily streaks",
      pt: "Sequencias diarias",
      nl: "Dagelijkse streaks",
      sv: "Dagliga streaks",
    },
    rules: [
      {
        en: "Logging in on consecutive weekdays increases the daily streak.",
        pt: "Entrar em dias uteis consecutivos aumenta a sequencia diaria.",
        nl: "Inloggen op opeenvolgende weekdagen verhoogt de dagelijkse streak.",
        sv: "Inloggning pa efterfoljande vardagar okar den dagliga streaken.",
      },
      {
        en: "Saturdays and Sundays do not reset the streak, increase it, or grant streak bonus points.",
        pt: "Sabados e domingos nao reiniciam a sequencia, nao aumentam a ofensiva e nao dao bonus de ofensiva.",
        nl: "Zaterdagen en zondagen resetten de streak niet, verhogen hem niet en geven geen streakbonus.",
        sv: "Lordagar och sondagar nollstaller inte streaken, okar den inte och ger ingen streakbonus.",
      },
      {
        en: "The streak appears on the map with the flame indicator.",
        pt: "A sequencia aparece no mapa com o indicador de chama.",
        nl: "De streak verschijnt op de kaart met de vlamindicator.",
        sv: "Streaken visas på kartan med flamindikatorn.",
      },
    ],
  },
  {
    icon: "flag",
    title: {
      en: "Hubs and boards",
      pt: "Hubs e rankings",
      nl: "Hubs en ranglijsten",
      sv: "Hubbar och topplistor",
    },
    rules: [
      {
        en: "During signup, each player chooses a hub: Netherlands, Sweden, Brasil or China.",
        pt: "No cadastro, cada jogador escolhe um hub: Netherlands, Sweden, Brasil ou China.",
        nl: "Tijdens registratie kiest elke speler een hub: Netherlands, Sweden, Brasil of China.",
        sv: "Vid registrering väljer varje spelare en hubb: Netherlands, Sweden, Brasil eller China.",
      },
      {
        en: "The hub flag appears beside the player name after login.",
        pt: "A bandeira do hub aparece ao lado do nome do jogador apos o login.",
        nl: "De hubvlag verschijnt naast de spelersnaam na het inloggen.",
        sv: "Hubbflaggan visas bredvid spelarens namn efter inloggning.",
      },
      {
        en: "Leaderboards include global, hub and crew boards, based only on real score data.",
        pt: "Os rankings incluem listas globais, por hub e por crew, baseadas apenas em dados reais de pontuacao.",
        nl: "Ranglijsten bevatten wereldwijde, hub- en crewranglijsten, alleen gebaseerd op echte scoregegevens.",
        sv: "Topplistor inkluderar globala listor, hubblistor och crewlistor, baserade enbart på verkliga poängdata.",
      },
    ],
  },
  {
    icon: "users",
    title: {
      en: "Crews",
      pt: "Crews",
      nl: "Crews",
      sv: "Crews",
    },
    rules: [
      {
        en: "Any player can create a crew and invite others.",
        pt: "Qualquer jogador pode criar uma crew e convidar outras pessoas.",
        nl: "Elke speler kan een crew maken en anderen uitnodigen.",
        sv: "Alla spelare kan skapa en crew och bjuda in andra.",
      },
      {
        en: "Invitations expire after 24 hours; expired invitations require a new request.",
        pt: "Convites expiram depois de 24 horas; convites expirados exigem uma nova solicitacao.",
        nl: "Uitnodigingen verlopen na 24 uur; verlopen uitnodigingen vereisen een nieuw verzoek.",
        sv: "Inbjudningar går ut efter 24 timmar; utgångna inbjudningar kräver en ny förfrågan.",
      },
      {
        en: "Crews can have 2 to 4 members.",
        pt: "Crews podem ter de 2 a 4 membros.",
        nl: "Crews kunnen 2 tot 4 leden hebben.",
        sv: "Crews kan ha 2 till 4 medlemmar.",
      },
      {
        en: "Crew members can come from any hub.",
        pt: "Membros da crew podem vir de qualquer hub.",
        nl: "Crewleden mogen uit elke hub komen.",
        sv: "Crewmedlemmar kan komma fran valfri hubb.",
      },
      {
        en: "More than one crew member can share the same hub.",
        pt: "Mais de um membro da crew pode ter o mesmo hub.",
        nl: "Meerdere crewleden mogen dezelfde hub hebben.",
        sv: "Flera crewmedlemmar kan ha samma hubb.",
      },
      {
        en: "Crews can include only players at the same island level.",
        pt: "Crews so podem incluir jogadores no mesmo nivel de ilha.",
        nl: "Crews kunnen alleen spelers op hetzelfde eilandniveau bevatten.",
        sv: "Crews kan bara innehålla spelare på samma önivå.",
      },
      {
        en: "A crew advances only when all members have advanced from the required island.",
        pt: "Uma crew so avanca quando todos os membros avancaram da ilha exigida.",
        nl: "Een crew gaat pas verder wanneer alle leden vanaf het vereiste eiland zijn doorgeschoven.",
        sv: "En crew går vidare först när alla medlemmar har gått vidare från den krävda ön.",
      },
    ],
  },
  {
    icon: "clipboard-list",
    title: {
      en: "Self assessment",
      pt: "Autoavaliacao",
      nl: "Zelfbeoordeling",
      sv: "Självskattning",
    },
    rules: [
      {
        en: "The first self assessment can be completed only once per player.",
        pt: "A primeira autoavaliacao pode ser concluida apenas uma vez por jogador.",
        nl: "De eerste zelfbeoordeling kan slechts één keer per speler worden voltooid.",
        sv: "Den första självskattningen kan bara slutföras en gång per spelare.",
      },
      {
        en: "It calibrates the starting experience and cannot be repeated to farm points.",
        pt: "Ela calibra a experiencia inicial e nao pode ser repetida para acumular pontos.",
        nl: "Deze kalibreert de startervaring en kan niet worden herhaald om punten te verzamelen.",
        sv: "Den kalibrerar startupplevelsen och kan inte göras om för att samla poäng.",
      },
      {
        en: "The final reassessment is separate and stays locked until the last required island is complete.",
        pt: "A reavaliacao final e separada e fica bloqueada ate a ultima ilha obrigatoria ser concluida.",
        nl: "De laatste herbeoordeling is apart en blijft vergrendeld tot het laatste vereiste eiland is voltooid.",
        sv: "Den slutliga självskattningen är separat och är låst tills den sista nödvändiga ön är klar.",
      },
      {
        en: "The final reassessment uses the hardest question level in the game: Stage 3 questions from the latest and highest-complexity content.",
        pt: "A reavaliacao final usa o nivel mais dificil de perguntas do jogo: questoes do Stage 3 do conteudo mais avancado e de maior complexidade.",
        nl: "De laatste herbeoordeling gebruikt het moeilijkste vragenniveau in het spel: Stage 3-vragen uit de nieuwste en meest complexe content.",
        sv: "Den slutliga sjalvskattningen anvander spelets svaraste frageniva: Stage 3-fragor fran det senaste och mest komplexa innehallet.",
      },
      {
        en: "At the final gate, the player must retake the assessment and score 90% or higher to qualify for victory.",
        pt: "No portao final, o jogador precisa refazer a avaliacao e tirar 90% ou mais para se qualificar para a vitoria.",
        nl: "Bij de laatste poort moet de speler de beoordeling opnieuw doen en 90% of hoger halen om voor de overwinning te kwalificeren.",
        sv: "Vid slutporten måste spelaren göra om självskattningen och få 90% eller mer för att kvalificera sig för seger.",
      },
    ],
  },
  {
    icon: "crown",
    title: {
      en: "Victory gate",
      pt: "Portao de vitoria",
      nl: "Overwinningspoort",
      sv: "Segerport",
    },
    rules: [
      {
        en: "A solo player can win after completing every required island, passing the final reassessment and declaring victory.",
        pt: "Um player solo pode vencer depois de concluir todas as ilhas obrigatorias, passar na reavaliacao final e declarar vitoria.",
        nl: "Een solospeler kan winnen nadat elk vereist eiland is voltooid, de laatste herbeoordeling is gehaald en de overwinning is verklaard.",
        sv: "En solospelare kan vinna efter att ha slutfört varje nödvändig ö, klarat den slutliga självskattningen och deklarerat seger.",
      },
      {
        en: "A crew wins only when every crew member completes every required island, passes the final reassessment with 90% or higher and declares victory.",
        pt: "Uma crew vence apenas quando todos os membros concluem todas as ilhas obrigatorias, passam na reavaliacao final com 90% ou mais e declaram vitoria.",
        nl: "Een crew wint pas wanneer elk crewlid elk vereist eiland voltooit, de laatste herbeoordeling met 90% of hoger haalt en de overwinning verklaart.",
        sv: "En crew vinner först när varje crewmedlem slutför varje nödvändig ö, klarar den slutliga självskattningen med 90% eller mer och deklarerar seger.",
      },
      {
        en: "The victory page shows declaration status by member, the winning player, and the top scorer inside the winning crew.",
        pt: "A pagina de vitoria mostra o status de declaracao por membro, o player vencedor e quem mais pontuou dentro da crew vencedora.",
        nl: "De overwinningspagina toont de verklaringsstatus per lid, de winnende speler en de topscorer binnen de winnende crew.",
        sv: "Segersidan visar deklarationsstatus per medlem, vinnande spelare och toppscorern i den vinnande crewn.",
      },
      {
        en: "Winning crews are released 10 days after victory. After that, members wait for the next version while new islands are discovered.",
        pt: "Crews vencedoras sao liberadas 10 dias apos a vitoria. Depois disso, os membros aguardam a proxima versao enquanto novas ilhas sao descobertas.",
        nl: "Winnende crews worden 10 dagen na de overwinning vrijgegeven. Daarna wachten leden op de volgende versie terwijl nieuwe eilanden worden ontdekt.",
        sv: "Vinnande crews släpps 10 dagar efter segern. Därefter väntar medlemmarna på nästa version medan nya öar upptäcks.",
      },
    ],
  },
  {
    icon: "store",
    title: {
      en: "Island items",
      pt: "Itens da ilha",
      nl: "Eilanditems",
      sv: "Öföremål",
    },
    rules: [
      {
        en: "Items unlock through the current level and total score.",
        pt: "Itens sao liberados pelo nivel atual e pela pontuacao total.",
        nl: "Items worden ontgrendeld via het huidige niveau en de totale score.",
        sv: "Föremål låses upp genom aktuell nivå och totalpoäng.",
      },
      {
        en: "Unlocked items can be added, removed or repositioned on the island.",
        pt: "Itens liberados podem ser adicionados, removidos ou reposicionados na ilha.",
        nl: "Ontgrendelde items kunnen worden toegevoegd, verwijderd of verplaatst op het eiland.",
        sv: "Upplåsta föremål kan läggas till, tas bort eller flyttas på ön.",
      },
      {
        en: "Animals and decorations can change as score and level grow.",
        pt: "Animais e decoracoes podem mudar conforme pontuacao e nivel aumentam.",
        nl: "Dieren en decoraties kunnen veranderen naarmate score en niveau groeien.",
        sv: "Djur och dekorationer kan förändras när poäng och nivå ökar.",
      },
    ],
  },
];

function localized(text: LocalizedText, language: LanguageCode) {
  return text[language] || text.en;
}

@Component({
  selector: "app-rules-page",
  standalone: true,
  imports: [CommonModule, LanguageSelectorComponent, LucideDynamicIcon],
  template: `
    <div class="min-h-screen bg-background bg-ocean-pattern">
      <header
        class="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md"
      >
        <div class="container mx-auto max-w-4xl px-4 py-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex min-w-0 items-center gap-4">
              <button
                [class]="buttonClasses('ghost', 'icon')"
                (click)="router.navigateByUrl('/')"
                [title]="copy.backToMap"
              >
                <svg lucideIcon="arrow-left" class="h-5 w-5"></svg>
              </button>
              <div class="flex min-w-0 items-center gap-3">
                <div class="rounded-md bg-primary/10 p-2">
                  <svg
                    lucideIcon="book-open"
                    class="h-6 w-6 text-primary"
                  ></svg>
                </div>
                <div class="min-w-0">
                  <h1 class="text-xl font-bold text-foreground">
                    {{ copy.headerTitle }}
                  </h1>
                  <p class="text-sm text-muted-foreground">
                    {{ copy.headerSubtitle }}
                  </p>
                </div>
              </div>
            </div>
            <app-language-selector
              [compact]="true"
              className="bg-background/80"
            />
          </div>
        </div>
      </header>

      <main class="container mx-auto max-w-4xl px-4 py-8">
        <section [class]="cardClasses('mb-6 border-primary/30 bg-primary/5')">
          <div class="p-6">
            <div class="flex flex-wrap items-center gap-2">
              <span [class]="badgeClasses('secondary', 'gap-1')">
                <svg lucideIcon="flame" class="h-3.5 w-3.5"></svg>
                {{ copy.streakBadge }}
              </span>
              <span [class]="badgeClasses('outline', 'gap-1')">
                <svg lucideIcon="flag" class="h-3.5 w-3.5"></svg>
                {{ copy.hubBadge }}
              </span>
              <span [class]="badgeClasses('outline', 'gap-1')">
                <svg lucideIcon="users" class="h-3.5 w-3.5"></svg>
                {{ copy.crewBadge }}
              </span>
            </div>
            <p class="mt-4 text-sm leading-6 text-muted-foreground">
              {{ copy.intro }}
            </p>
          </div>
        </section>

        <section class="grid gap-4 md:grid-cols-2">
          <article
            *ngFor="let section of sections"
            [class]="cardClasses('h-full')"
          >
            <header class="flex flex-col space-y-1.5 p-6 pb-3">
              <h2
                class="flex items-center gap-2 text-lg font-semibold leading-none tracking-normal"
              >
                <svg
                  [lucideIcon]="section.icon"
                  class="h-5 w-5 text-primary"
                ></svg>
                {{ localize(section.title) }}
              </h2>
            </header>
            <div class="p-6 pt-0">
              <ul class="space-y-3 text-sm leading-6 text-muted-foreground">
                <li *ngFor="let rule of section.rules" class="flex gap-2">
                  <svg
                    lucideIcon="circle-check-big"
                    class="mt-0.5 h-4 w-4 shrink-0 text-accent"
                  ></svg>
                  <span>{{ localize(rule) }}</span>
                </li>
              </ul>
            </div>
          </article>
        </section>
      </main>
    </div>
  `,
})
export class RulesPageComponent {
  readonly router = inject(Router);
  readonly language = inject(LanguageService);

  readonly sections = RULE_SECTIONS;

  get copy() {
    return RULES_COPY[this.language.language] || RULES_COPY.en;
  }

  localize(text: LocalizedText) {
    return localized(text, this.language.language);
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  badgeClasses = badgeClasses;
}
